import type {
  ExecutionAttestation,
  ExecutionPrincipal,
  ExternalResourceOperation,
  ExternalResourceRef,
  GovernanceRuntimeEvidence,
  MutationGovernanceResolver,
} from "@fabricorg/platform/governance";
import type { DatabricksPrincipal } from "./identity.js";
import { databricksWorkspaceOrigin } from "./workspace-origin.js";

/** Durable generation of the Harness-to-Platform Databricks governance bridge. */
export const DATABRICKS_PLATFORM_GOVERNANCE_BRIDGE_VERSION = "1";

/** Runtime evidence applications pass directly to `createGovernedActionHost`. */
export function databricksGovernanceRuntimeEvidence(
  overrides: Pick<GovernanceRuntimeEvidence, "hostPackageVersion" | "policyRulesetVersion"> = {},
): Partial<GovernanceRuntimeEvidence> {
  return {
    ...overrides,
    providerBridge: {
      name: "@fabric-harness/databricks",
      version: DATABRICKS_PLATFORM_GOVERNANCE_BRIDGE_VERSION,
    },
  };
}

export interface DatabricksPlatformResource {
  resourceType: string;
  resourceId: string;
  operation: ExternalResourceOperation;
  displayName?: string;
  dataClassifications?: string[];
  metadata?: Record<string, unknown>;
}

export interface DatabricksMutationGovernanceOptions {
  workspaceHost: string;
  principal: DatabricksPrincipal;
  resolveResources: (
    input: Parameters<MutationGovernanceResolver>[0],
  ) => Promise<DatabricksPlatformResource[]> | DatabricksPlatformResource[];
  principalId?: string;
}

export function databricksPlatformResourceRef(
  workspaceHost: string,
  resource: Omit<DatabricksPlatformResource, "operation" | "dataClassifications">,
): ExternalResourceRef {
  return {
    provider: "databricks",
    resourceType: resource.resourceType,
    resourceId: resource.resourceId,
    ...(resource.displayName ? { displayName: resource.displayName } : {}),
    metadata: {
      workspaceHost: databricksWorkspaceOrigin(workspaceHost),
      ...(resource.metadata ?? {}),
    },
  };
}

/** Audit-safe Databricks identity. Tokens and secrets are deliberately never represented. */
export function databricksExecutionPrincipal(
  principal: DatabricksPrincipal,
  delegatedBy?: ExecutionPrincipal,
  explicitId?: string,
): ExecutionPrincipal {
  const principalId = explicitId ?? principalLabel(principal);
  return {
    principalId,
    principalType:
      principal.kind === "on-behalf-of" || principal.kind === "cli-profile"
        ? "natural_person"
        : "service_account",
    provider: "databricks",
    ...(delegatedBy ? { delegatedBy } : {}),
    metadata: { authenticationKind: principal.kind },
  };
}

/** Platform resolver backed by Databricks resource identities but no Databricks network calls. */
export function createDatabricksMutationGovernanceResolver(
  options: DatabricksMutationGovernanceOptions,
): MutationGovernanceResolver {
  return async (input) => {
    const resources = await options.resolveResources(input);
    return {
      footprint: {
        version: 1,
        resources: resources.map((resource) => ({
          resource: databricksPlatformResourceRef(options.workspaceHost, resource),
          operation: resource.operation,
          ...(resource.dataClassifications
            ? { dataClassifications: resource.dataClassifications }
            : {}),
        })),
      },
      executionPrincipal: databricksExecutionPrincipal(
        options.principal,
        { principalId: input.actorId, principalType: input.actorType },
        options.principalId,
      ),
    };
  };
}

export interface DatabricksAttestationInput {
  operation: string;
  externalOperationId: string;
  startedAt: Date | string;
  completedAt?: Date | string;
  workspaceHost?: string;
  resource?: Omit<DatabricksPlatformResource, "operation" | "dataClassifications">;
  satisfiesObligations?: string[];
  evidenceUri?: string;
  metadata?: Record<string, unknown>;
}

export function databricksExecutionAttestation(
  input: DatabricksAttestationInput,
): ExecutionAttestation {
  return {
    provider: "databricks",
    operation: input.operation,
    externalOperationId: input.externalOperationId,
    ...(input.resource && input.workspaceHost
      ? { resource: databricksPlatformResourceRef(input.workspaceHost, input.resource) }
      : {}),
    evidenceReferences: [{
      referenceType: "external_observation",
      referenceId: input.externalOperationId,
      ...(input.evidenceUri ? { uri: input.evidenceUri } : {}),
      metadata: { provider: "databricks", operation: input.operation },
    }],
    ...(input.satisfiesObligations
      ? { satisfiesObligations: input.satisfiesObligations }
      : {}),
    startedAt: input.startedAt,
    ...(input.completedAt ? { completedAt: input.completedAt } : {}),
    ...(input.metadata ? { metadata: input.metadata } : {}),
  };
}

function principalLabel(principal: DatabricksPrincipal): string {
  switch (principal.kind) {
    case "service-principal":
      return principal.clientId;
    case "cli-profile":
      return principal.profile ?? "databricks-cli-user";
    case "on-behalf-of":
      return principal.label ?? "databricks-on-behalf-of-user";
    case "pat":
      return principal.label ?? "databricks-pat-principal";
  }
}
